import { Box, Divider, Flex, SimpleGrid, Spinner, Stat, StatHelpText, StatLabel, StatNumber, Tag, Text } from '@chakra-ui/react'
import React, { useEffect, useState } from 'react'
import { StatCard } from '../StatsCard'
import { LuEye, LuFileText, LuGlobe, LuMessageSquare, LuTarget, LuTrendingUp } from 'react-icons/lu'
import { AiOutlineCloseCircle } from 'react-icons/ai'
import { TemplateUsageByType } from './TemplateUsageByType'
import { ChannelPerformanceOverview } from './ChannelPerformanceOverview'
import { TbActivityHeartbeat } from 'react-icons/tb'
import TemplateTypeUsageGrowth from './TemplateTypeUsageGrowthChart'
import TemplatePerformanceTable from './TemplatePerformanceTable'
import TemplateMessageBreakdown from './TemplateMessageBreakdown'
import moment from 'moment'
import API from '../../../helpers/axiosInstance'
import { formatNumber } from '../../../utils/DefaultFunction'

const Template = ({ date, refresh }) => {
    const [data, setTemplateDate] = useState(null);
    const [loading, setLoading] = useState(false);

    const getApiHeaders = () => ({
        "x-api-key": localStorage.getItem("api-key"),
        "x-access-token": localStorage.getItem("customer-portal"),
    });

    const dateformated = moment(date);
    const month = dateformated.format("MM");
    const year = dateformated.format("YYYY");

    const fetchEvents = async () => {
        setLoading(true);
        try {
            const url = `${process.env.REACT_APP_API_URL}/dashboard/customer/templates-overview?month=${month}&year=${year}${refresh}`;
            const response = await API.get(url, { headers: getApiHeaders() });

            setTemplateDate(response.data);
        } catch (error) {
            console.error("Error fetching analytics:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date]);

    const header = data?.data?.header;

    const statCards = [
        {
            title: "Total Templates",
            value: formatNumber(header?.total_templates, "en-US", true) ?? 0,
            icon: <LuFileText />,
            iconColor: "#64748b",
            change: "0 active this week",
        },
        {
            title: "Messages Sent",
            value: formatNumber(header?.total_messages_sent, "en-US", true) ?? 0,
            icon: <LuMessageSquare />,
            iconColor: "#64748b",
            change: "Across all templates",
        },
        {
            title: "Failed Messages",
            value: formatNumber(header?.failed_messages, "en-US", true) ?? 0,
            icon: <AiOutlineCloseCircle />,
            iconColor: "#fb2c36",
            changeColor: "#fb2c36",
            change: `${header?.failure_rate}% failure rate`,
        },
        {
            title: "Avg Delivery Rate",
            value: `${header?.avg_delivery_rate ?? 0}%`,
            icon: <LuTarget />,
            iconColor: "#64748b",
            changeColor: "#00a63e",
            change: "All channels combined",
        },
        {
            title: "Avg Read Rate",
            value: `${formatNumber(header?.avg_read_rate, "en-US", true) ?? 0}%`,
            icon: <LuEye />,
            iconColor: "#64748b",
            changeColor: "#155dfc",
            change: "For readable channels",
        },
        {
            title: "Channels Used",
            value: header?.channels_used ?? 0,
            icon: <LuGlobe />,
            iconColor: "#64748b",
            change: "Active communication channels",
        },
    ];


    if (loading || !data?.data) {
        return (
            <Flex justify="center" align="center" h="300px">
                <Spinner size="lg" />
            </Flex>
        );
    }
    return (
        <Box>
            <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 3, xl: 6 }} spacing={2}>
                {statCards.map((card, idx) => (
                    <StatCard
                        key={idx}
                        title={card.title}
                        value={card.value}
                        change={card.change}
                        icon={card.icon}
                        iconColor={card.iconColor}
                        changeColor={card.changeColor}
                    />
                ))}
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} mt={4}>
                <TemplateUsageByType data={data?.data?.template_usage_by_type} />
                <ChannelPerformanceOverview data={data?.data?.channel_performance_overview} />
            </SimpleGrid>

            {data?.data?.template_type_usage_growth && (
                <Box className="box-style-card" my={4}>
                    <Flex direction="column">
                        <Flex align="center" gap={2}>
                            <TbActivityHeartbeat size={20} color="#155dfc" />
                            <Text fontSize="md" fontWeight="semibold" color="gray.700">
                                Template Type Usage Growth
                            </Text>
                        </Flex>
                        <Text fontSize="sm" color="#64748b">6-month trend showing message volume growth by template type</Text>
                        <SimpleGrid columns={{ base: 1, lg: 3, xl: 4 }} spacing={6} mt={4} mb={4}>
                            <Box className={"box-style-card"} flex="1" bg={"white"} p={{ base: 3, md: 4, lg: "10px", xl: "15px" }}>
                                <Stat w={"100%"}>
                                    <StatLabel display="flex" alignItems="center" gap={2} justifyContent={"space-between"} mb={3}>
                                        <Flex align={"center"}>
                                            <Box w="15px" h="15px" borderRadius="full" bg={"#f97316"} me={2} /> Authentication
                                        </Flex>
                                        <Box fontSize="lg" color={"#00a63e"}><LuTrendingUp /></Box>
                                    </StatLabel>
                                    <StatNumber color={"#f97316"} fontWeight={"bold"}>
                                        {formatNumber(data?.data?.template_type_usage_growth?.current_month_cards?.authentication?.current, "en-US", true) ?? 0}
                                    </StatNumber>
                                    <Text fontSize="xs" color="gray.500">Current month volume</Text>
                                    <StatHelpText color={"gray.600"} fontWeight="normal" fontSize={"xs"} mt={2}>
                                        <Flex align={"center"}>
                                            <Tag bg={"#10b981"} color={"white"} fontSize={"xs"} size={"sm"} fontWeight={"400"} me={2}>
                                                {data?.data?.template_type_usage_growth?.current_month_cards?.authentication?.change ?? 0}%
                                            </Tag> vs 6 months ago
                                        </Flex>
                                    </StatHelpText>
                                </Stat>
                            </Box>

                            <Box className={"box-style-card"} flex="1" bg={"white"} p={{ base: 3, md: 4, lg: "10px", xl: "15px" }}>
                                <Stat w={"100%"}>
                                    <StatLabel display="flex" alignItems="center" gap={2} justifyContent={"space-between"} mb={3}>
                                        <Flex align={"center"}>
                                            <Box w="15px" h="15px" borderRadius="full" bg={"#10b981"} me={2} /> Transaction
                                        </Flex>
                                        <Box fontSize="lg" color={"#00a63e"}><LuTrendingUp /></Box>
                                    </StatLabel>
                                    <StatNumber color={"#10b981"} fontWeight={"bold"}>
                                        {formatNumber(data?.data?.template_type_usage_growth?.current_month_cards?.transaction?.current, "en-US", true) ?? 0}
                                    </StatNumber>
                                    <Text fontSize="xs" color="gray.500">Current month volume</Text>
                                    <StatHelpText color={"gray.600"} fontWeight="normal" fontSize={"xs"} mt={2}>
                                        <Flex align={"center"}>
                                            <Tag bg={"#10b981"} color={"white"} fontSize={"xs"} fontWeight={"400"} size={"sm"} me={2}>
                                                {data?.data?.template_type_usage_growth?.current_month_cards?.transaction?.change ?? 0}%
                                            </Tag> vs 6 months ago
                                        </Flex>
                                    </StatHelpText>
                                </Stat>
                            </Box>

                            <Box className={"box-style-card"} flex="1" bg={"white"} p={{ base: 3, md: 4, lg: "10px", xl: "15px" }}>
                                <Stat w={"100%"}>
                                    <StatLabel display="flex" alignItems="center" gap={2} justifyContent={"space-between"} mb={3}>
                                        <Flex align={"center"}>
                                            <Box w="15px" h="15px" borderRadius="full" bg={"var(--brand-primary)"} me={2} /> Informational
                                        </Flex>
                                        <Box fontSize="lg" color={"#00a63e"}><LuTrendingUp /></Box>
                                    </StatLabel>
                                    <StatNumber color={"var(--brand-primary)"} fontWeight={"bold"}>
                                        {formatNumber(data?.data?.template_type_usage_growth?.current_month_cards?.informational?.current, "en-US", true) ?? 0}
                                    </StatNumber>
                                    <Text fontSize="xs" color="gray.500">Current month volume</Text>
                                    <StatHelpText color={"gray.600"} fontWeight="normal" fontSize={"xs"} mt={2}>
                                        <Flex align={"center"}>
                                            <Tag bg={"#10b981"} color={"white"} fontSize={"xs"} fontWeight={"400"} me={2} size={"sm"}>
                                                {data?.data?.template_type_usage_growth?.current_month_cards?.informational?.change ?? 0}%
                                            </Tag> vs 6 months ago
                                        </Flex>
                                    </StatHelpText>
                                </Stat>
                            </Box>

                            <Box className={"box-style-card"} flex="1" bg={"white"} p={{ base: 3, md: 4, lg: "10px", xl: "15px" }}>
                                <Stat w={"100%"}>
                                    <StatLabel display="flex" alignItems="center" gap={2} justifyContent={"space-between"} mb={3}>
                                        <Flex align={"center"}>
                                            <Box w="15px" h="15px" borderRadius="full" bg={"#8b5cf6"} me={2} /> Marketing
                                        </Flex>
                                        <Box fontSize="lg" color={"#00a63e"}><LuTrendingUp /></Box>
                                    </StatLabel>
                                    <StatNumber color={"#8b5cf6"} fontWeight={"bold"}>
                                        {formatNumber(data?.data?.template_type_usage_growth?.current_month_cards?.marketing?.current, "en-US", true) ?? 0}
                                    </StatNumber>
                                    <Text fontSize="xs" color="gray.500">Current month volume</Text>
                                    <StatHelpText color={"gray.600"} fontWeight="normal" fontSize={"xs"} mt={2}>
                                        <Flex align={"center"}>
                                            <Tag bg={"#10b981"} color={"white"} fontSize={"xs"} fontWeight={"400"} me={2} size={"sm"}>
                                                {data?.data?.template_type_usage_growth?.current_month_cards?.marketing?.change ?? 0}%
                                            </Tag> vs 6 months ago
                                        </Flex>
                                    </StatHelpText>
                                </Stat>
                            </Box>
                        </SimpleGrid>
                    </Flex>
                    <TemplateTypeUsageGrowth growthData={data?.data?.template_type_usage_growth} />
                    <Divider my={8} />
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="100%">
                        {/* Fastest Growing Card */}
                        <Box
                            bg="#eaf3fdeb"
                            borderRadius="lg"
                            p={3}


                        >
                            <Flex align="center" gap={2} mb={2}>
                                <LuTrendingUp size={18} color="var(--brand-900)" />
                                <Text fontSize="sm" fontWeight="600" color="var(--brand-900)">
                                    Fastest Growing
                                </Text>
                            </Flex>
                            <Text fontSize="sm" color="#193cb8">
                                {`${data?.data.template_type_usage_growth?.fastest_growing?.name} templates showing ${data?.data.template_type_usage_growth?.fastest_growing?.growth_percent}% growth over the last 6 months`}
                            </Text>
                        </Box>


                        <Box
                            bg="#f0fdf4"
                            borderRadius="lg"
                            p={3}


                        >
                            <Flex align="center" gap={2} mb={2}>
                                <LuTrendingUp size={18} color="#0d542b" />
                                <Text fontSize="sm" fontWeight="semibold" color="#0d542b">
                                    Total Growth
                                </Text>
                            </Flex>
                            <Text fontSize="sm" color="#016630">
                                {`Overall template usage increased by ${data?.data.template_type_usage_growth?.total_growth?.growth_percent}% (${formatNumber(data?.data.template_type_usage_growth?.total_growth?.from, "en-US", true)} → ${formatNumber(data?.data.template_type_usage_growth?.total_growth?.to, "en-US", true)} messages/month)`}
                            </Text>
                        </Box>
                    </SimpleGrid>
                </Box>
            )}

            {data?.data?.template_performance_details && (
                <Box className="box-style-card" my={4}>
                    <Flex direction="column" mb={4}>
                        <Flex align="center" gap={2}>
                            <TbActivityHeartbeat size={20} color="#155dfc" />
                            <Text fontSize="md" fontWeight="semibold" color="gray.700">
                                Template Performance Details
                            </Text>
                        </Flex>
                        <Text fontSize="sm" color="#64748b">Detailed metrics for each template with channel breakdown</Text>
                    </Flex>
                    <TemplatePerformanceTable templatePerformanceDetails={data?.data?.template_performance_details} />
                </Box>
            )}


            {data?.data?.template_message_breakdown && (
                <TemplateMessageBreakdown templateMessageBreakdown={data?.data?.template_message_breakdown} />
            )}
        </Box>
    )
}

export default Template